import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHead } from "@/components/PageHead";
import { POLL } from "@/lib/format";
import { memo, useEffect, useRef, useState } from "react";

import { link, type DebugNode, type LogLine, type MsgRow } from "@/api/client";
import { useCursorTail } from "@/lib/hooks/useCursorTail";
import { Chip } from "@/components/Chip";

import { Icon } from "@/components/icons";
/** Memoized log/message lines: the panes hold a 300-item sliding window and
 *  append on most 1.2s ticks for a chatty node — without memo (and with the old
 *  index keys, which SHIFT once the window slides) React rewrote all 300 DOM
 *  lines per append. Stable seq keys + memo = only new lines mount. */
const LogLineRow = memo(function LogLineRow({ l }: { l: LogLine }) {
  return (
    <div className="nd-line">
      <span className="nd-ts">{new Date(l.ts * 1000).toLocaleTimeString("en-GB")}</span>
      <span className={`nd-tag ${l.kind}`}>{l.kind.toUpperCase()}</span>
      {l.topic !== "—" && <span className="nd-topic">{l.topic}</span>}
      <span className="nd-txt">{l.text}</span>
    </div>
  );
});

const MsgLineRow = memo(function MsgLineRow({ m }: { m: MsgRow }) {
  return (
    <div className="nd-line">
      <span className="nd-ts">{new Date(m.ts * 1000).toLocaleTimeString("en-GB")}</span>
      <span className={`nd-tag ${m.dir}`}>{m.dir === "in" ? "RX" : "TX"}</span>
      <span className="nd-topic">{m.topic}</span>
      <span className="nd-txt" title={m.text}>
        q{m.qos} · {m.size}B · {m.hex}
      </span>
    </div>
  );
});

/** Debug workbench (OLK-17). Nodes live in the SERVER — a browser reload never
 *  loses them (they still hold real mTLS sessions). Frames are EPHEMERAL: nothing
 *  here reaches Vault. */
export function DebugPage() {
  const qc = useQueryClient();
  const idents = useQuery({ queryKey: ["link", "identities"], queryFn: link.identities });
  const nodes = useQuery({ queryKey: ["link", "nodes"], queryFn: link.nodes, refetchInterval: POLL.CONSOLE });

  const [kind, setKind] = useState<"telematics" | "server">("telematics");
  const [identity, setIdentity] = useState("");
  const [keepalive, setKeepalive] = useState(30);
  const [subTopic, setSubTopic] = useState("t/CMP-001/d/+/#");
  const [subQos, setSubQos] = useState(1);

  const pool = kind === "telematics" ? idents.data?.telematics : idents.data?.server;
  useEffect(() => {
    if (pool?.length && !pool.includes(identity)) setIdentity(pool[0]);
  }, [pool, identity]);

  const create = useMutation({
    mutationFn: () =>
      link.createNode({
        kind,
        identity,
        keepalive,
        subs: subTopic ? [{ topic: subTopic, qos: subQos }] : [],
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["link"] }),
  });

  const list = nodes.data?.nodes ?? [];
  const enabled = nodes.data?.enabled ?? false;

  // aggregate per-topic queue stats across all nodes
  const agg = new Map<string, { pub: number; recv: number; bo: number; bi: number; qos: number | null; last: number | null }>();
  for (const n of list) {
    for (const t of n.topics) {
      const a = agg.get(t.topic) ?? { pub: 0, recv: 0, bo: 0, bi: 0, qos: null, last: null };
      a.pub += t.published;
      a.recv += t.received;
      a.bo += t.bytes_out;
      a.bi += t.bytes_in;
      if (t.last_qos !== null) a.qos = t.last_qos;
      if (t.last_seen && (!a.last || t.last_seen > a.last)) a.last = t.last_seen;
      agg.set(t.topic, a);
    }
  }

  return (
    <div className="mpage on">
      <PageHead eyebrow="Link · Debug Test" title="Protocol workbench"
        right={<Chip tone="warn">BENCH ONLY · EPHEMERAL — nothing is stored in Vault</Chip>} />

      <div className="panel panel-pad" style={{ marginBottom: 14 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))",
            gap: 12,
            alignItems: "end",
          }}
        >
          <div>
            <label className="simlab">Node type</label>
            <select
              className="search fill"
             
              value={kind}
              onChange={(e) => setKind(e.target.value as "telematics" | "server")}
            >
              <option value="telematics">Telematics node (device)</option>
              <option value="server">Server node (platform side)</option>
            </select>
          </div>
          <div>
            <label className="simlab">
              {pool?.length ? "Identity (cert CN)" : "Client ID (password mode — no PKI yet)"}
            </label>
            {pool?.length ? (
              <select
                className="search fill"
               
                value={identity}
                onChange={(e) => setIdentity(e.target.value)}
              >
                {pool.map((i) => (
                  <option key={i}>{i}</option>
                ))}
              </select>
            ) : (
              <input
                className="search fill"
               
                placeholder="client id, e.g. svc-app"
                value={identity}
                onChange={(e) => setIdentity(e.target.value)}
              />
            )}
          </div>
          <div>
            <label className="simlab">Keep-alive (s)</label>
            <input
              className="search fill"
             
              type="number"
              min={5}
              max={600}
              value={keepalive}
              onChange={(e) => setKeepalive(Number(e.target.value) || 30)}
            />
          </div>
          <div>
            <label className="simlab">Subscribe on start</label>
            <input
              className="search fill"
             
              value={subTopic}
              onChange={(e) => setSubTopic(e.target.value)}
            />
          </div>
          <div>
            <label className="simlab">QoS</label>
            <select
              className="search fill"
             
              value={subQos}
              onChange={(e) => setSubQos(Number(e.target.value))}
            >
              <option>0</option>
              <option>1</option>
              <option>2</option>
            </select>
          </div>
          <div>
            <button
              className="btn teal"
              style={{ width: "100%" }}
              disabled={!enabled || !identity || create.isPending}
              onClick={() => create.mutate()}
            >
              + Create node
            </button>
          </div>
        </div>
        {!enabled && (
          <div style={{ marginTop: 10, fontSize: 11.5, color: "var(--warn)" }}>
            Debug nodes are DISABLED (ORBIT_ALLOW_DEBUG_NODES=false) — the production default.
          </div>
        )}
      </div>

      {list.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--t-dim)", padding: "10px 4px" }}>
          No nodes yet — create one above. Each becomes a real mTLS client on the broker.
        </div>
      ) : (
        list.map((n) => <NodeCard key={n.id} node={n} />)
      )}

      <div className="section-label">Message queues — per topic, across all nodes</div>
      <div className="tblwrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Topic</th>
              <th>Published</th>
              <th>Received</th>
              <th>Bytes out</th>
              <th>Bytes in</th>
              <th>Last QoS</th>
              <th>Last activity</th>
            </tr>
          </thead>
          <tbody>
            {agg.size === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: 16, color: "var(--t-dim)" }}>
                  No traffic yet — create a node and publish.
                </td>
              </tr>
            ) : (
              [...agg.entries()].sort().map(([topic, a]) => (
                <tr key={topic}>
                  <td className="mono">{topic}</td>
                  <td className="num">{a.pub}</td>
                  <td className="num">{a.recv}</td>
                  <td className="num">{a.bo}</td>
                  <td className="num">{a.bi}</td>
                  <td>{a.qos === null ? "—" : `qos${a.qos}`}</td>
                  <td>{a.last ? new Date(a.last * 1000).toLocaleTimeString("en-GB") : "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const STATE_TONE: Record<DebugNode["state"], "ok" | "bad" | "warn" | "mute"> = {
  online: "ok",
  killed: "bad",
  error: "bad",
  connecting: "warn",
  stopped: "mute",
};

function NodeCard({ node }: { node: DebugNode }) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["link"] });
  const logEl = useRef<HTMLDivElement>(null);

  const [pubTopic, setPubTopic] = useState(
    node.kind === "telematics" ? `tel/live/${node.identity}` : "tel/cmd/test/bench-001",
  );
  const [payload, setPayload] = useState('{"schema_ver":1,"seq":1,"snapshot":true,"soc":71.5}');
  const [view, setView] = useState<"log" | "msgs">("log");
  const [collapsed, setCollapsed] = useState(false);
  const [full, setFull] = useState(false);
  const [msgFilter, setMsgFilter] = useState("");
  const msgEl = useRef<HTMLDivElement>(null);
  const [pubQos, setPubQos] = useState(1);
  const [retain, setRetain] = useState(false);
  const [newSub, setNewSub] = useState("");
  const [newSubQos, setNewSubQos] = useState(1);

  // protocol log (CONNECT/SUBACK/PUBLISH/PINGREQ…) — shared hardened tail
  const { items: lines, clear: clearLines } = useCursorTail<LogLine>({
    fetch: async (c) => (await link.log(node.id, (c as number | undefined) ?? 0)).lines,
    cursorOf: (l) => l.seq,
    max: 300,
    intervalMs: 1200,
    resetKey: node.id,
  });

  useEffect(() => {
    if (logEl.current) logEl.current.scrollTop = logEl.current.scrollHeight;
  }, [lines]);

  // live messages pane — shared hardened tail; only polls while the msgs view
  // is open, and node/filter changes reset the window
  const { items: msgs, clear: clearMsgs } = useCursorTail<MsgRow>({
    fetch: async (c) => (await link.messages(node.id, (c as number | undefined) ?? 0, msgFilter)).messages,
    cursorOf: (m) => m.seq,
    max: 300,
    intervalMs: 1200,
    paused: view !== "msgs",
    resetKey: `${node.id}|${msgFilter}`,
  });

  useEffect(() => {
    if (msgEl.current) msgEl.current.scrollTop = msgEl.current.scrollHeight;
  }, [msgs]);

  const live = node.state === "online";

  return (
    <div
      className="nd-card"
      style={
        full
          ? {
              position: "fixed",
              inset: 12,
              zIndex: 1000,
              margin: 0,
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 8px 40px rgba(0,0,0,.35)",
            }
          : undefined
      }
    >
      <div className="nd-head">
        <span className={`nd-kind ${node.kind === "telematics" ? "tele" : "srv"}`}>
          {node.kind === "telematics" ? "TELEMATICS" : "SERVER"}
        </span>
        <span className="nd-cn">{node.identity}</span>
        <Chip tone={STATE_TONE[node.state]}>{node.state.toUpperCase()}</Chip>
        <span className="nd-meta">
          keepalive {node.keepalive}s
          {node.uptime_s != null && ` · up ${node.uptime_s}s`} · in {node.msgs_in} / out{" "}
          {node.msgs_out}
          {node.error && ` · ${node.error}`}
        </span>
        <span style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
          {/* actions: Start | Stop · Kill · Clear */}
          {live ? (
            <>
              <button className="btn red" onClick={() => link.stop(node.id).then(invalidate)}>
                Stop
              </button>
              <button
                className="btn red"
                title="drop the socket without DISCONNECT — broker notices only after ~1.5× keepalive"
                onClick={() => link.kill(node.id).then(invalidate)}
              >
                Kill
              </button>
            </>
          ) : (
            <button className="btn teal" onClick={() => link.start(node.id).then(invalidate)}>
              <Icon name="play" /> Start
            </button>
          )}
          <button
            className="btn ghost"
            title={`clear ${view === "log" ? "protocol log" : "messages"} (buffer + view)`}
            onClick={() => {
              void link.clear(node.id, view === "msgs" ? "messages" : "log");
              if (view === "log") clearLines(); else clearMsgs();
            }}
          >
            Clear
          </button>

          {/* separator */}
          <span style={{ width: 1, height: 18, background: "var(--line2)", margin: "0 3px" }} />

          {/* window controls: minimize · maximize · close */}
          <button
            className="btn ghost"
            title={collapsed ? "expand" : "minimize"}
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? "▸" : "▾"}
          </button>
          <button
            className="btn ghost"
            title={full ? "exit full screen" : "maximize"}
            onClick={() => {
              setFull(!full);
              setCollapsed(false);
            }}
          >
            <Icon name={full ? "close" : "fullscreen"} />
          </button>
          <button className="btn ghost" title="close (remove node)" onClick={() => link.remove(node.id).then(invalidate)}>
            <Icon name="close" />
          </button>
        </span>
      </div>

      {!collapsed && (
      <div className="nd-body" style={full ? { flex: 1, minHeight: 0, overflow: "hidden" } : undefined}>
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center" }}>
            <button className={`btn ${view === "log" ? "teal" : "ghost"}`} onClick={() => setView("log")}>
              Protocol log
            </button>
            <button className={`btn ${view === "msgs" ? "teal" : "ghost"}`} onClick={() => setView("msgs")}>
              Messages
            </button>
            {view === "msgs" && (
              <input
                className="search"
                style={{ flex: 1, minWidth: 0 }}
                placeholder="topic filter — tel/his/+, tel/cmd/#, exact topic… (empty = all)"
                value={msgFilter}
                onChange={(e) => setMsgFilter(e.target.value)}
              />
            )}
          </div>
          {view === "log" ? (
            <div className="nd-log" ref={logEl} style={full ? { flex: 1, height: "auto" } : undefined}>
              {lines.map((l) => (
                <LogLineRow key={l.seq} l={l} />
              ))}
            </div>
          ) : (
            <div className="nd-log" ref={msgEl} style={full ? { flex: 1, height: "auto" } : undefined}>
              {msgs.map((m) => (
                <MsgLineRow key={m.seq} m={m} />
              ))}
              {!msgs.length && (
                <div className="nd-line">
                  <span className="nd-txt">
                    no messages yet{msgFilter ? ` (filter: ${msgFilter})` : ""} — waiting…
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="nd-ctl" style={full ? { overflowY: "auto", minHeight: 0 } : undefined}>
          <div>
            <label className="simlab">Subscriptions</label>
            <div className="nd-subs">
              {node.subs.length ? (
                node.subs.map((s) => (
                  <span className="nd-sub" key={s.topic}>
                    <b>{s.topic}</b> q{s.qos}
                    <span
                      className="x"
                      onClick={() => link.unsubscribe(node.id, s.topic).then(invalidate)}
                    >
                      ✕
                    </span>
                  </span>
                ))
              ) : (
                <span style={{ fontSize: 10.5, color: "var(--t-dim)" }}>none</span>
              )}
            </div>
          </div>

          <div style={{ display: "flex", gap: 6 }}>
            <input
              className="search"
              style={{ flex: 1, minWidth: 0 }}
              placeholder="topic to subscribe"
              value={newSub}
              onChange={(e) => setNewSub(e.target.value)}
            />
            <select
              className="search"
              style={{ width: 62, minWidth: 0 }}
              value={newSubQos}
              onChange={(e) => setNewSubQos(Number(e.target.value))}
            >
              <option>0</option>
              <option>1</option>
              <option>2</option>
            </select>
            <button
              className="btn ghost"
              onClick={() =>
                newSub &&
                link.subscribe(node.id, { topic: newSub, qos: newSubQos }).then(() => {
                  setNewSub("");
                  invalidate();
                })
              }
            >
              Sub
            </button>
          </div>

          <div>
            <label className="simlab">Publish</label>
            <input
              className="search"
              style={{ width: "100%", minWidth: 0, marginBottom: 6 }}
              value={pubTopic}
              onChange={(e) => setPubTopic(e.target.value)}
            />
            <textarea
              className="search"
              // `fontFamily: "Consolas, monospace"` until 2026-10-10 — a
              // <textarea> does not inherit the page font, so this said it
              // out loud and was the last literal face in the tree. The
              // element reset in theme.css covers textareas now.
              style={{ width: "100%", minWidth: 0, height: 64, fontSize: 11,
                       fontVariantNumeric: "tabular-nums" }}
              value={payload}
              onChange={(e) => setPayload(e.target.value)}
            />
            <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6 }}>
              <select
                className="search"
                style={{ width: 62, minWidth: 0 }}
                value={pubQos}
                onChange={(e) => setPubQos(Number(e.target.value))}
              >
                <option>0</option>
                <option>1</option>
                <option>2</option>
              </select>
              <label
                style={{ fontSize: 10.5, color: "var(--t-mute)", display: "flex", gap: 4, alignItems: "center" }}
              >
                <input type="checkbox" checked={retain} onChange={(e) => setRetain(e.target.checked)} />{" "}
                retain
              </label>
              <button
                className="btn teal"
                style={{ marginLeft: "auto" }}
                onClick={() =>
                  link
                    .publish(node.id, { topic: pubTopic, payload, qos: pubQos, retain })
                    .then(invalidate)
                }
              >
                Publish
              </button>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
